import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FilterSelect from '../components/Documents/FilterSelect';
import DynamicTable from '../components/Documents/DynamicTable';
import UploadModal from '../components/Documents/UploadModal';
import Pagination from '../components/Documents/Pagination';
import { MagnifyingGlassIcon, CalendarIcon, MixerHorizontalIcon, DownloadIcon, PlusIcon } from '@radix-ui/react-icons';
import { getQuotaStatus } from '../services/api';
import api from '../services/api';
import config from '../config/config';

// Dynamic column and row interfaces based on API response
interface Column {
  class: string;
  header: string;
  key: string;
  show: number;
}

interface Row {
  [key: string]: any;
}

interface ApiResponse {
  columns: Column[];
  document_type: string;
  page: number;
  pages: number;
  per_page: number;
  rows: Row[];
  total: number;
}

// Available filter options
const dateRanges = ['All Time', 'This Month', 'Last Month', 'This Quarter', 'Last Quarter', 'This Year'];
const statusOptions = ['All Statuses', 'Paid', 'Pending', 'Overdue'];
const documentStatuses = ['All Statuses', 'Processed', 'Processing', 'Failed'];

const DataPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('All Statuses');
  const [selectedDateRange, setSelectedDateRange] = useState('All Time');
  // Track selected documents separately for each tab
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [selectedCashbooks, setSelectedCashbooks] = useState<string[]>([]);
  const [selectedVatBooks, setSelectedVatBooks] = useState<string[]>([]);
  const [selectedBankStatements, setSelectedBankStatements] = useState<string[]>([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('invoices');
  const perPageOptions = [10, 25, 50];
  
  // State for storing API data
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apiData, setApiData] = useState<ApiResponse | null>(null);
  const [currentTableData, setCurrentTableData] = useState<ApiResponse | null>(null);
  const [metricsData, setMetricsData] = useState({
    invoices: 0,
    cashbooks: 0,
    vatBooks: 0,
    bankStatements: 0
  });
  
  // State for drill-down functionality
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [drillDownData, setDrillDownData] = useState<{[key: string]: any}>({});
  const [loadingDrillDown, setLoadingDrillDown] = useState<{[key: string]: boolean}>({});
  const [quotaStatus, setQuotaStatus] = useState<any>(null);
  const [isLoadingQuota, setIsLoadingQuota] = useState(false);
  
  // Load quota status on component mount
  useEffect(() => {
    loadQuotaStatus();
  }, []);

  const loadQuotaStatus = async () => {
    setIsLoadingQuota(true);
    try {
      const status = await getQuotaStatus();
      setQuotaStatus(status);
    } catch (error) {
      console.error('Failed to load quota status:', error);
      setQuotaStatus(null);
    } finally {
      setIsLoadingQuota(false);
    }
  };
  
  // Get document type and id from URL query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const type = params.get('type');
    
    if (type) {
      switch (type) {
        case 'invoice':
          setActiveTab('invoices');
          break;
        case 'cashbook':
          setActiveTab('cashbooks');
          break;
        case 'vat-book':
          setActiveTab('vatBooks');
          break;
        case 'bank-statement':
          setActiveTab('bankStatements');
          break;
        default:
          setActiveTab('invoices');
      }
    }
  }, [location]);
  
  // Fetch documents data with pagination
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Build query params
        const queryParams = new URLSearchParams();
        queryParams.append('page', currentPage.toString());
        queryParams.append('per_page', itemsPerPage.toString());
        
        // Add document type filter based on active tab
        let docType = '';
        switch (activeTab) {
          case 'invoices':
            docType = 'invoice';
            break;
          case 'cashbooks':
            docType = 'cashbook';
            break;
          case 'vatBooks':
            docType = 'vat_book';
            break;
          case 'bankStatements':
            docType = 'bank_statement';
            break;
        }
        
        if (docType) {
          queryParams.append('type', docType);
        }
        
        // Add status filter if selected
        if (selectedStatus !== 'All Statuses') {
          queryParams.append('status', selectedStatus.toLowerCase());
        }
        
        // Add date range filter if selected
        if (selectedDateRange !== 'All Time') {
          queryParams.append('date_range', selectedDateRange);
        }
        
        // Add search term if provided
        if (searchTerm) {
          queryParams.append('search', searchTerm);
        }
        
        const response = await api.get(`${config.apiBaseUrl}/data/list?${queryParams.toString()}`);
        const data = response.data;
        
        // Debug: Log the API response to see the structure
        console.log('API Response:', data);
        console.log('Document type from API:', data.document_type);
        console.log('First row sample:', data.rows?.[0]);
        
        // Store the API response data
        setApiData(data);
        setCurrentTableData(data);
      } catch (error: any) {
        console.error('Error fetching documents:', error);
        // Check if it's a network error
        if (error && error.code === 'ERR_NETWORK') {
          setError('Network error: Unable to connect to the server. Please make sure the backend server is running.');
        } else {
          setError(error instanceof Error ? error.message : 'An unknown error occurred');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocuments();
    
    // Also fetch metrics
    fetchMetrics();
  }, [currentPage, itemsPerPage, activeTab, selectedStatus, selectedDateRange, searchTerm]);
  
  // Fetch metrics for the data page
  const fetchMetrics = async () => {
    try {
      const response = await api.get(`${config.apiBaseUrl}/data/metrics`);
      const data = response.data;
      // Update metrics data if available
      if (data && data.metrics) {
        setMetricsData(data.metrics);
        console.log('Metrics updated:', data.metrics);
      }
    } catch (error: any) {
      console.error('Error fetching metrics:', error);
      // Don't show error for metrics, as it's not critical for the page to function
    }
  };
  
  // Metrics data
  const metrics = {
    invoices: { count: metricsData.invoices, label: 'Invoices' },
    cashbooks: { count: metricsData.cashbooks, label: 'Cashbooks' },
    vatBooks: { count: metricsData.vatBooks, label: 'VAT Books' },
    bankStatements: { count: metricsData.bankStatements, label: 'Bank Statements' },
  };

  const handleSelectDocument = (id: string, checked: boolean) => {
    const currentSelected = getSelectedDocuments();
    console.log('Select document - ID:', id, 'Checked:', checked, 'Current selected:', currentSelected);
    
    if (checked) {
      setSelectedDocumentsForTab([...currentSelected, id]);
    } else {
      setSelectedDocumentsForTab(currentSelected.filter(rowId => rowId !== id));
    }
  };
  
  // Handle document click to navigate to detail view
  const handleDocumentClick = (rowId: string, type: string) => {
    console.log('Row ID:', rowId, 'Type:', type);
    
    // For VAT books, don't expand since we have actions in the main table
    if (type === 'vat_book') {
      return;
    }
    
    // Toggle drill-down instead of navigation for other document types
    toggleDrillDown(rowId, type);
  };

  // Toggle drill-down for a specific row
  const toggleDrillDown = async (rowId: string, type: string) => {
    const newExpandedRows = new Set(expandedRows);
    
    if (newExpandedRows.has(rowId)) {
      // Collapse the row
      newExpandedRows.delete(rowId);
      setExpandedRows(newExpandedRows);
      
      // Remove drill-down data
      const newDrillDownData = { ...drillDownData };
      delete newDrillDownData[rowId];
      setDrillDownData(newDrillDownData);
    } else {
      // Expand the row
      newExpandedRows.add(rowId);
      setExpandedRows(newExpandedRows);
      
      // Fetch drill-down data
      await fetchDrillDownData(rowId, type);
    }
  };

  // Fetch drill-down data from API
  const fetchDrillDownData = async (rowId: string, type: string) => {
    try {
      // Set loading state for this specific row
      setLoadingDrillDown(prev => ({ ...prev, [rowId]: true }));
      
      // Get the row data to find the document_id
      const rowData = getDataByTab().find((row: any) => row.id === rowId);
      if (!rowData) {
        console.error('Row data not found for ID:', rowId);
        return;
      }
      
      const documentId = rowData.document_id;
      if (!documentId) {
        console.error('Row missing document_id:', rowData);
        return;
      }
      
      // Convert type to match backend expectations
      const backendType = type === 'vat_book' ? 'vat_book' : type;
      
      const response = await api.post(`${config.apiBaseUrl}/data/drill-down`, {
        document_type: backendType,
        row_id: rowId,
        document_id: documentId
      });
      
      const drillData = response.data;
      setDrillDownData(prev => ({ ...prev, [rowId]: drillData }));
      
    } catch (error: any) {
      console.error('Error fetching drill-down data:', error);
      // Set error state for this row
      setDrillDownData(prev => ({ 
        ...prev, 
        [rowId]: { error: error.message || 'Failed to load drill-down data' }
      }));
    } finally {
      setLoadingDrillDown(prev => ({ ...prev, [rowId]: false }));
    }
  };

  // Check if a row is expanded
  const isRowExpanded = (rowId: string) => {
    return expandedRows.has(rowId);
  };

  // Get drill-down data for a specific row
  const getDrillDownData = (rowId: string) => {
    return drillDownData[rowId] || null;
  };

  // Check if drill-down is loading for a specific row
  const isDrillDownLoading = (rowId: string) => {
    return loadingDrillDown[rowId] || false;
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const data = getDataByTab(); // Use getDataByTab() to get the actual row data
      
      if (!Array.isArray(data)) {
        setSelectedDocumentsForTab([]);
        return;
      }
      
      // Use the actual row IDs (invoice IDs, cashbook IDs, etc.)
      const allIds = data.map((item: any) => item.id).filter(id => id !== undefined);
      console.log('Select all - row IDs:', allIds);
      setSelectedDocumentsForTab(allIds);
    } else {
      setSelectedDocumentsForTab([]);
    }
  };

  const handleExport = async () => {
    const selectedIds = getSelectedDocuments();
    if (selectedIds.length === 0) return;
    
    try {
      // Get the document type based on active tab
      const tabToTypeMap: { [key: string]: string } = {
        'invoices': 'invoice',
        'cashbooks': 'cashbook',
        'vatBooks': 'vat_book',
        'bankStatements': 'bank_statement'
      };
      
      const documentType = tabToTypeMap[activeTab];
      
      // Get the actual row data for selected IDs (these are invoice IDs, cashbook IDs, etc.)
      const selectedRows = getDataByTab().filter((row: any) => selectedIds.includes(row.id));
      
      // Extract the actual row IDs (not document IDs)
      const rowIds = selectedRows.map((row: any) => row.id);
      
      console.log('Selected row IDs for export:', rowIds);
      console.log('Document type for export:', documentType);
      
      // Prepare the request payload
      const payload = {
        document_type: documentType,
        selected_ids: rowIds, // These are the actual row IDs (invoice IDs, cashbook IDs, etc.)
        filters: {
          status: selectedStatus,
          date_range: selectedDateRange,
          search: searchTerm
        }
      };
      
      // Call the server-side export API
      const response = await api.post(`${config.apiBaseUrl}/data/export`, payload, {
        responseType: 'blob' // Important: Set response type to blob for file download
      });
      
      // Create a download link for the Excel file
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      link.download = `${documentType}_export_${timestamp}.xlsx`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error: any) {
      console.error('Export error:', error);
      // You can add a toast notification here if you have one
      alert('Export failed. Please try again.');
    }
  };

  const handleExportItems = async () => {
    const selectedIds = getSelectedDocuments();
    if (selectedIds.length === 0) return;
    
    try {
      // Get the document type based on active tab
      const tabToTypeMap: { [key: string]: string } = {
        'invoices': 'invoice',
        'cashbooks': 'cashbook',
        'vatBooks': 'vat_book',
        'bankStatements': 'bank_statement'
      };
      
      const documentType = tabToTypeMap[activeTab];
      
      // Get the actual row data for selected IDs (these are invoice IDs, cashbook IDs, etc.)
      const selectedRows = getDataByTab().filter((row: any) => selectedIds.includes(row.id));
      
      // Extract the actual row IDs (not document IDs)
      const rowIds = selectedRows.map((row: any) => row.id);
      
      console.log('Selected row IDs for items export:', rowIds);
      console.log('Document type for items export:', documentType);
      
      // Prepare the request payload
      const payload = {
        document_type: documentType,
        selected_ids: rowIds, // These are the actual row IDs (invoice IDs, cashbook IDs, etc.)
        filters: {
          status: selectedStatus,
          date_range: selectedDateRange,
          search: searchTerm
        }
      };
      
      // Call the server-side export items API
      const response = await api.post(`${config.apiBaseUrl}/data/export-items`, payload, {
        responseType: 'blob' // Important: Set response type to blob for file download
      });
      
      // Create a download link for the Excel file
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      link.download = `${documentType}_items_export_${timestamp}.xlsx`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error: any) {
      console.error('Export items error:', error);
      // You can add a toast notification here if you have one
      alert('Export items failed. Please try again.');
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Helper functions to get and set selected documents based on active tab
  const getSelectedDocuments = (): string[] => {
    const selected = (() => {
      switch (activeTab) {
        case 'invoices':
          return selectedInvoices;
        case 'cashbooks':
          return selectedCashbooks;
        case 'vatBooks':
          return selectedVatBooks;
        case 'bankStatements':
          return selectedBankStatements;
        default:
          return selectedInvoices;
      }
    })();
    
    console.log(`Selected documents for ${activeTab}:`, selected);
    return selected;
  };

  const setSelectedDocumentsForTab = (ids: string[]) => {
    console.log(`Setting selected documents for ${activeTab}:`, ids);
    
    switch (activeTab) {
      case 'invoices':
        setSelectedInvoices(ids);
        break;
      case 'cashbooks':
        setSelectedCashbooks(ids);
        break;
      case 'vatBooks':
        setSelectedVatBooks(ids);
        break;
      case 'bankStatements':
        setSelectedBankStatements(ids);
        break;
      default:
        setSelectedInvoices(ids);
    }
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedStatus, selectedDateRange, activeTab]);


  // Handle download of page Excel file
  const handleDownloadPageExcel = async (vatEntryId: string, weekEnding: string | null) => {
    try {
      // Call the backend endpoint to get the download URL
      const response = await api.post(`${config.apiBaseUrl}/data/download-vat-page`, {
        vat_entry_id: vatEntryId,
        week_ending: weekEnding
      });
      
      const { download_url, filename } = response.data;
      
      if (!download_url) {
        alert('No Excel file available for this entry');
        return;
      }
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = download_url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error: any) {
      console.error('Download error:', error);
      alert('Download failed. Please try again.');
    }
  };

  // Render drill-down content based on document type
  const renderDrillDownContent = (rowId: string, type: string) => {
    const drillData = getDrillDownData(rowId);
    const isLoading = isDrillDownLoading(rowId);
    
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      );
    }
    
    if (!drillData || drillData.error) {
      return (
        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
          {drillData?.error || 'No drill-down data available'}
        </div>
      );
    }
    
    if (type === 'invoice' && drillData.data && drillData.data.length > 0) {
      return (
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-100 dark:bg-gray-800">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Item No</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Item Name</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Description</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Quantity</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Unit</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Net Price</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Net Worth</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">VAT %</th>
                                                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Gross Worth</th>
                                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Hours</th>
                                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Page</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
              {drillData.data.map((item: any) => (
                <tr key={item.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{item.item_no || ''}</td>
                  <td className="px-6 py-4 whitespace-normal text-sm text-gray-900 dark:text-gray-200">{item.item_name}</td>
                  <td className="px-6 py-4 whitespace-normal text-sm text-gray-900 dark:text-gray-200">{item.description || ''}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-right">{item.quantity || ''}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{item.unit_measure || ''}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-right">{item.net_price ? item.net_price : ''}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-right">{item.net_worth ? item.net_worth : ''}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-right">{item.vat_percentage ? `${item.vat_percentage}%` : ''}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-right">{item.gross_worth ? item.gross_worth : ''}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{item.hours || ''}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{item.page_number || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    
    if (type === 'vat_book' && drillData.data && drillData.data.length > 0) {
      return (
        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
          No drill-down available for VAT book entries
        </div>
      );
    }
    
    // For other document types, show the data in a simple format
    if (drillData.data && drillData.data.length > 0) {
      return (
        <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
            {type === 'cashbook' ? 'Cashbook Entry Details' :
             type === 'bank_statement' ? 'Bank Statement Details' : 'Details'}
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(drillData.data[0]).map(([key, value]) => (
              <div key={key} className="bg-white dark:bg-gray-800 p-3 rounded border">
                <div className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                  {key.replace(/_/g, ' ')}
                </div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-200">
                  {value !== null && value !== undefined ? String(value) : 'N/A'}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    return (
      <div className="text-center py-4 text-gray-500 dark:text-gray-400">
        No additional details available
      </div>
    );
  };

  // Add document_type to each row based on active tab for navigation
  const getDataWithDocumentType = () => {
    const data = getDataByTab();
    if (!Array.isArray(data)) return [];
    
    const tabToTypeMap: { [key: string]: string } = {
      'invoices': 'invoice',
      'cashbooks': 'cashbook',
      'vatBooks': 'vat_book',
      'bankStatements': 'bank_statement'
    };
    
    const documentType = tabToTypeMap[activeTab] || 'document';
    console.log('Adding document_type to rows:', documentType, 'for tab:', activeTab);
    
    return data.map(row => ({
      ...row,
      document_type: documentType
    }));
  };



  // Get data based on active tab - now using server-side pagination
  const getDataByTab = () => {
    if (!apiData || !apiData.rows) return [];
    
    // Check if the current tab matches the document type
    const tabToTypeMap: { [key: string]: string } = {
      'invoices': 'invoice',
      'cashbooks': 'cashbook',
      'vatBooks': 'vat_book',
      'bankStatements': 'bank_statement'
    };
    
    if (apiData.document_type === tabToTypeMap[activeTab]) {
      return apiData.rows;
    }
    
    return [];
  };

  // Use server-side pagination
  const totalPages = apiData?.pages || 0;


  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <div className="text-red-600 dark:text-red-400 mb-4">{error}</div>
        <button
          onClick={() => window.location.reload()}
          className="text-primary hover:underline"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">Data</h1>
        <div className="flex space-x-3">
          <button
            className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md shadow-sm ${
              getSelectedDocuments().length > 0 
                ? 'text-white bg-primary hover:bg-blue-600' 
                : 'text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 dark:border-gray-600'
            } focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 focus:ring-primary`}
            onClick={handleExport}
            disabled={getSelectedDocuments().length === 0}
          >
            <DownloadIcon className="w-5 h-5 mr-1" />
            Export Summary
          </button>
          {activeTab === 'invoices' && (
            <button
              className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md shadow-sm ${
                getSelectedDocuments().length > 0 
                  ? 'text-white bg-green-600 hover:bg-green-700' 
                  : 'text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 dark:border-gray-600'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 focus:ring-green-500`}
              onClick={handleExportItems}
              disabled={getSelectedDocuments().length === 0}
            >
              <DownloadIcon className="w-5 h-5 mr-1" />
              Export Items
            </button>
          )}

          <button
            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 focus:ring-primary ${
              quotaStatus?.quota_exceeded 
                ? 'text-gray-400 bg-gray-300 dark:bg-gray-600 cursor-not-allowed' 
                : 'text-white bg-primary hover:bg-blue-600'
            }`}
            onClick={() => setIsUploadModalOpen(true)}
            disabled={quotaStatus?.quota_exceeded}
            title={quotaStatus?.quota_exceeded ? `Quota exceeded. You have used ${quotaStatus.pages_used}/${quotaStatus.pages_quota} pages.` : ''}
          >
            <PlusIcon className="w-5 h-5 mr-1" />
            Upload Document
          </button>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(metrics).map(([key, value]) => (
          <div 
            key={key}
            className={`bg-white dark:bg-dark-sidebar p-4 rounded-lg shadow border-l-4 ${
              activeTab === key ? 'border-primary' : 'border-gray-200 dark:border-gray-700'
            } cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors`}
            onClick={() => {
              setActiveTab(key);
              setCurrentPage(1);
            }}
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{value.label}</p>
                <p className="text-2xl font-semibold">{value.count}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex">
          <button
            className={`py-2 px-4 border-b-2 font-medium text-sm ${
              activeTab === 'invoices'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
            onClick={() => {
              setActiveTab('invoices');
              setCurrentPage(1);
            }}
          >
            Invoices
          </button>
          <button
            className={`py-2 px-4 border-b-2 font-medium text-sm ${
              activeTab === 'cashbooks'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
            onClick={() => {
              setActiveTab('cashbooks');
              setCurrentPage(1);
            }}
          >
            Cashbooks
          </button>
          <button
            className={`py-2 px-4 border-b-2 font-medium text-sm ${
              activeTab === 'vatBooks'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
            onClick={() => {
              setActiveTab('vatBooks');
              setCurrentPage(1);
            }}
          >
            VAT Books
          </button>
          <button
            className={`py-2 px-4 border-b-2 font-medium text-sm ${
              activeTab === 'bankStatements'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
            onClick={() => {
              setActiveTab('bankStatements');
              setCurrentPage(1);
            }}
          >
            Bank Statements
          </button>
        </nav>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-3">
          <FilterSelect 
            options={dateRanges}
            value={selectedDateRange}
            onChange={setSelectedDateRange}
            placeholder="Date Range"
            icon={<CalendarIcon className="h-4 w-4" />}
          />
          
          <FilterSelect 
            options={documentStatuses}
            value={selectedStatus}
            onChange={setSelectedStatus}
            placeholder="Status"
            icon={<MixerHorizontalIcon className="h-4 w-4" />}
          />
        </div>
        <div className="flex items-center bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 w-64">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-300 mr-2" />
          <input
            type="text"
            placeholder="Search by name..."
            className="flex-1 outline-none text-sm text-gray-700 dark:text-gray-200 bg-transparent placeholder-gray-400 dark:placeholder-gray-400"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">Show</span>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className="border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 focus:outline-none focus:ring-primary focus:border-primary"
          >
            {perPageOptions.map(option => (
              <option key={option} value={option} className="dark:bg-gray-700">{option}</option>
            ))}
          </select>
          <span className="text-sm text-gray-500 dark:text-gray-400">entries</span>
        </div>
        {getSelectedDocuments().length > 0 && (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {getSelectedDocuments().length} {getSelectedDocuments().length === 1 ? 'item' : 'items'} selected
          </div>
        )}
      </div>
      
      {apiData && apiData.columns && apiData.rows && (
        <DynamicTable 
          columns={apiData.columns}
          rows={getDataWithDocumentType()}
          selectedDocuments={getSelectedDocuments()}
          onSelectDocument={handleSelectDocument}
          onSelectAll={handleSelectAll}
          onDocumentClick={handleDocumentClick}
          isRowExpanded={isRowExpanded}
          renderDrillDownContent={renderDrillDownContent}
          onDownloadExcel={handleDownloadPageExcel}
        />
      )}

      <Pagination 
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
      
      <UploadModal 
        open={isUploadModalOpen} 
        onOpenChange={setIsUploadModalOpen}
        onUploadSuccess={() => {
          // Refresh quota status after upload
          loadQuotaStatus();
          // Trigger a re-fetch by updating the current page
          setCurrentPage(1);
        }}
      />
    </div>
  );
};

export default DataPage;
